/**
 * Policy Rules Viewer Component
 * Displays XACML policy rules in a structured, readable format
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { MessageSquare, Filter, EyeOff, Settings } from 'lucide-react'
import type { ParsedXacmlRule, ParsedXacmlCondition, ParsedXacmlObligation } from '@/types/policy'
import { AttributesInline } from './policy-attributes-viewer'
import { detectObligationType } from './obligation-editors'
import { getFunctionDisplayName, getDataTypeName, formatAttributeValue, hasAttributes } from '@/lib/xacml-parser'
import { cn } from '@/lib/utils'

/**
 * Props for PolicyRulesViewer
 */
interface PolicyRulesViewerProps {
  rules: ParsedXacmlRule[]
  title?: string
  className?: string
}

/**
 * Get badge variant for rule effect
 */
function getEffectBadgeVariant(effect: 'Permit' | 'Deny'): 'default' | 'destructive' {
  return effect === 'Permit' ? 'default' : 'destructive'
}

/**
 * Display a single condition
 */
function ConditionDisplay({ condition }: { condition: ParsedXacmlCondition }) {
  const functionName = getFunctionDisplayName(condition.functionId)
  const dataTypeName = getDataTypeName(condition.dataType)

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="font-mono text-muted-foreground">{condition.attributeId}</span>
      {condition.negated && (
        <Badge variant="destructive" className="text-xs">NOT</Badge>
      )}
      <span className="text-muted-foreground">{functionName}</span>
      <Badge variant="outline" className="font-mono">
        {formatAttributeValue(condition.value, condition.dataType)}
      </Badge>
      <span className="text-xs text-muted-foreground">({dataTypeName})</span>
    </div>
  )
}

/**
 * Display conditions list
 */
function ConditionsDisplay({ conditions }: { conditions: ParsedXacmlCondition[] }) {
  if (conditions.length === 0) {
    return (
      <span className="text-sm text-muted-foreground italic">
        No additional conditions
      </span>
    )
  }

  return (
    <div className="space-y-1">
      {conditions.map((condition, index) => (
        <ConditionDisplay key={index} condition={condition} />
      ))}
    </div>
  )
}

/**
 * Icon and label for an obligation type
 */
const OBLIGATION_TYPE_CONFIG: Record<ReturnType<typeof detectObligationType>, { icon: typeof Settings; label: string; color: string }> = {
  message: { icon: MessageSquare, label: 'Message', color: 'text-blue-500' },
  'row-filter': { icon: Filter, label: 'Row Filter', color: 'text-amber-500' },
  mask: { icon: EyeOff, label: 'Mask Column', color: 'text-purple-500' },
  generic: { icon: Settings, label: 'Generic', color: 'text-muted-foreground' },
}

/**
 * Display a single obligation in read-only format
 */
function ObligationItem({ obligation }: { obligation: ParsedXacmlObligation }) {
  const type = detectObligationType(obligation.obligationId)
  const config = OBLIGATION_TYPE_CONFIG[type]
  const Icon = config.icon

  return (
    <div className="p-3 rounded-md border bg-muted/30 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className={cn('h-4 w-4', config.color)} />
        <Badge variant="secondary" className="text-xs">{config.label}</Badge>
        <Badge variant="outline" className="text-xs">
          {obligation.fulfillOn === 'Permit' ? 'On Permit' : 'On Deny'}
        </Badge>
      </div>
      {obligation.attributeAssignments.length > 0 && (
        <div className="space-y-1 pl-6">
          {obligation.attributeAssignments.map((aa, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="font-mono text-muted-foreground text-xs">
                {aa.attributeId.split(':').pop()}
              </span>
              <span className="text-muted-foreground">=</span>
              <Badge variant="outline" className="font-mono text-xs">
                {String(aa.value) || '(empty)'}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Display obligations list
 */
function ObligationsDisplay({ obligations }: { obligations: ParsedXacmlObligation[] }) {
  return (
    <div className="space-y-2">
      {obligations.map((obligation, index) => (
        <ObligationItem key={index} obligation={obligation} />
      ))}
    </div>
  )
}

/**
 * Single rule card display
 */
function RuleCard({
  rule,
  index,
}: {
  rule: ParsedXacmlRule
  index: number
}) {
  const hasTarget = rule.target && hasAttributes(rule.target)
  const hasConditions = rule.conditions.length > 0
  const hasObligations = (rule.obligations ?? []).length > 0

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      {/* Rule Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Rule {index + 1}:</span>
            <span className="font-mono font-medium text-sm truncate">
              {rule.ruleId}
            </span>
            <Badge variant={getEffectBadgeVariant(rule.effect)}>
              {rule.effect}
            </Badge>
          </div>
          {rule.description && (
            <p className="mt-1 text-sm text-muted-foreground">
              {rule.description}
            </p>
          )}
        </div>
      </div>

      {/* Rule Target (if any) */}
      {hasTarget && rule.target && (
        <>
          <Separator />
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Rule Target
            </span>
            <AttributesInline attributes={rule.target} />
          </div>
        </>
      )}

      {/* Rule Conditions (if any) */}
      {hasConditions && (
        <>
          <Separator />
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Conditions
            </span>
            <ConditionsDisplay conditions={rule.conditions} />
          </div>
        </>
      )}

      {/* Rule Obligations (if any) */}
      {hasObligations && (
        <>
          <Separator />
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Obligations
            </span>
            <ObligationsDisplay obligations={rule.obligations} />
          </div>
        </>
      )}

      {/* Show message if no target, conditions, or obligations */}
      {!hasTarget && !hasConditions && !hasObligations && (
        <>
          <Separator />
          <p className="text-sm text-muted-foreground italic">
            This rule applies unconditionally to matching policy targets.
          </p>
        </>
      )}
    </div>
  )
}

/**
 * Main Policy Rules Viewer component
 */
export function PolicyRulesViewer({
  rules,
  title = 'Policy Rules',
  className,
}: PolicyRulesViewerProps) {
  if (rules.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground italic">
            No rules defined in this policy.
          </p>
        </CardContent>
      </Card>
    )
  }

  // Count permit and deny rules
  const permitCount = rules.filter((r) => r.effect === 'Permit').length
  const denyCount = rules.filter((r) => r.effect === 'Deny').length

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{title}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{rules.length} rule{rules.length !== 1 ? 's' : ''}</Badge>
            {permitCount > 0 && (
              <Badge variant="default" className="text-xs">
                {permitCount} Permit
              </Badge>
            )}
            {denyCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {denyCount} Deny
              </Badge>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Rules are evaluated in order. The first matching rule determines the access decision.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {rules.map((rule, index) => (
            <RuleCard key={rule.ruleId || index} rule={rule} index={index} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Compact rules summary (for list views)
 */
export function RulesSummary({
  rules,
  className,
}: {
  rules: ParsedXacmlRule[]
  className?: string
}) {
  if (rules.length === 0) {
    return (
      <span className={cn('text-sm text-muted-foreground italic', className)}>
        No rules
      </span>
    )
  }

  const permitCount = rules.filter((r) => r.effect === 'Permit').length
  const denyCount = rules.filter((r) => r.effect === 'Deny').length

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Badge variant="secondary">{rules.length} rule{rules.length !== 1 ? 's' : ''}</Badge>
      {permitCount > 0 && (
        <Badge variant="default" className="text-xs">
          {permitCount} Permit
        </Badge>
      )}
      {denyCount > 0 && (
        <Badge variant="destructive" className="text-xs">
          {denyCount} Deny
        </Badge>
      )}
    </div>
  )
}

export default PolicyRulesViewer
